import React, { useState } from "react";
import "./ParticipantsList.scss";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import { ImageResizable } from "../ImageResizeable";
import { useTranslation } from "react-i18next";
import { useIsAuthenticated } from "../../../utils/auth";
import { canRouteToReserve } from "../../../utils/tripStatus";

const ParticipantMoreInfoCard = ({ open, handleClose, participant, tripId, trip }) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: 350,
          width: 350,
          bgcolor: "white",
          borderRadius: 5,
          p: 4,
          overflowY: "scroll",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div
            style={{
              borderRadius: "50%",
              width: "130px",
              height: "130px",
              marginBottom: "1rem",
              overflow: "hidden",
            }}
          >
            <ImageResizable
              src={participant.image}
              alt={participant.display_name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              size="s"
            />
          </div>
          <div
            style={{
              color: "black",
              fontSize: "1.2rem",
              fontWeight: "bold",
              width: "90%",
            }}
          >
            {t("book_this_trip_to_interact_with", {
              defaultValue: `Book this trip to interact with ${participant.display_name}!`,
              participant_first_name: participant.display_name,
            })}
          </div>
          <button
            style={{
              fontFamily: "Manrope, sans-serif",
              fontWeight: "700",
              fontSize: "16px",
              color: "white",
              backgroundColor: "dodgerblue",
              textTransform: "unset",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "2.7rem",
              width: "9rem",
              marginTop: "1.3rem",
              borderRadius: "10px",
            }}
            onClick={() => {
              // A sold-out / waitlist date dead-ends on the reserve page, so send
              // the user to the trip's date listing (where the Waitlist action
              // lives) instead of /reserve.
              if (canRouteToReserve(trip)) {
                window.location.href = `/reserve?trip_id=${tripId}`;
              } else {
                window.location.href = `/trips/${trip?.project_id}`;
              }
            }}
          >
            {t("book_this_trip", {
              defaultValue: "Book this trip",
            })}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export const ParticipantsList = ({ participants, tripId, trip }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useIsAuthenticated();

  const [open, setOpen] = useState(false);
  const [participant, setParticipant] = useState(null);

  const handleOpen = (participant) => {
    setOpen(true);
    setParticipant(participant);
    // add to window history stack
    window.history.pushState(
      {
        modal: "opened",
      },
      "",
      ""
    );
  };
  const handleClose = () => {
    setOpen(false);
    setParticipant(null);
  };

  const handleSignIn = () => {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?reroute=${encodeURIComponent(currentPath)}`;
  };

  // When not authenticated: show first 3 clearly, blur next 20 (indices 3-22)
  const visibleCount = isAuthenticated ? participants.length : 3;
  const blurredCount = isAuthenticated ? 0 : Math.min(20, Math.max(0, participants.length - 3));
  const visibleParticipants = participants.slice(0, visibleCount);
  const blurredParticipants = isAuthenticated ? [] : participants.slice(3, 3 + blurredCount);

  return (
    <div
      className="participants-list"
      style={!isAuthenticated && blurredParticipants.length > 0 ? { overflow: "hidden" } : {}}
    >
      {visibleParticipants.map((participant, index) => (
        <div key={index} className="participant-card" onClick={() => handleOpen(participant)}>
          <div className="participant-photo-container">
            <ImageResizable
              src={participant.image}
              alt={participant.display_name}
              className="participant-photo"
              size="s"
            />
          </div>

          <div className="participant-info">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="participant-name">{participant.display_name}</div>
              {(participant.influencer_type || participant.leader > 0) && (
                <div className="participant-leader">
                  <StarOutlinedIcon style={{ fontSize: "14px", marginRight: "3px" }} />
                  <span>
                    {participant.influencer_type ? t("special_guest") : t("flock_leader")}
                  </span>
                </div>
              )}
            </div>
            <div className="participant-stats">
              {Number(participant.review_count) > 0 && (
                <span>
                  {t("positive_reviews_count", {
                    defaultValue: `${Number(participant.review_count)} ${
                      Number(participant.review_count) > 1 ? "positive reviews" : "positive review"
                    }`,
                    count: Number(participant.review_count),
                  })}
                </span>
              )}
              {participant.skill_level && (
                <>
                  {" • "}
                  <span>
                    {participant.skill_level.charAt(0).toUpperCase() +
                      participant.skill_level.slice(1)}{" "}
                  </span>
                </>
              )}
            </div>
            <div className="participant-description">{participant.description}</div>
          </div>
        </div>
      ))}

      {!isAuthenticated && blurredParticipants.length > 0 && (
        <>
          <div className="tw-my-6 tw-flex tw-justify-center">
            <button
              onClick={handleSignIn}
              className="tw-bg-dodgerblue tw-rounded-lg tw-px-6 tw-py-3 tw-font-semibold tw-text-white tw-shadow-lg tw-transition-colors tw-duration-200 hover:tw-bg-blue-600"
              style={{
                fontFamily: "Manrope, sans-serif",
                fontWeight: "700",
                fontSize: "16px",
                backgroundColor: "dodgerblue",
              }}
            >
              {t("sign_in_to_view_all_participants", {
                defaultValue: "Sign in to view all participants",
              })}
            </button>
          </div>

          <div
            className="tw-pointer-events-none tw-relative tw-w-full tw-select-none"
            style={{ filter: "blur(8px)" }}
          >
            {blurredParticipants.map((participant, index) => (
              <div key={index + visibleCount} className="participant-card">
                <div className="participant-photo-container">
                  <ImageResizable
                    src={participant.image}
                    alt={participant.display_name}
                    className="participant-photo"
                    size="s"
                  />
                </div>

                <div className="participant-info">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div className="participant-name">{participant.display_name}</div>
                    {(participant.influencer_type || participant.leader > 0) && (
                      <div className="participant-leader">
                        <StarOutlinedIcon style={{ fontSize: "14px", marginRight: "3px" }} />
                        <span>
                          {participant.influencer_type ? t("special_guest") : t("flock_leader")}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="participant-stats">
                    {Number(participant.review_count) > 0 && (
                      <span>
                        {t("positive_reviews_count", {
                          defaultValue: `${Number(participant.review_count)} ${
                            Number(participant.review_count) > 1
                              ? "positive reviews"
                              : "positive review"
                          }`,
                          count: Number(participant.review_count),
                        })}
                      </span>
                    )}
                    {participant.skill_level && (
                      <>
                        {" • "}
                        <span>
                          {participant.skill_level.charAt(0).toUpperCase() +
                            participant.skill_level.slice(1)}{" "}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="participant-description">{participant.description}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {participant && open && (
        <ParticipantMoreInfoCard
          open={open}
          handleClose={handleClose}
          participant={participant}
          tripId={tripId}
          trip={trip}
        />
      )}
    </div>
  );
};
